import { env } from '@/lib/env';
import { handleSessionExpired } from '@/lib/auth-expired';
import { tryRefreshSession } from '@/lib/auth-session';
import { useAuthStore } from '@/features/auth/auth.store';

/** In dev, use Vite's `/api` proxy (same origin). In production, use the configured API URL. */
const BASE_URL = import.meta.env.DEV ? '' : env.VITE_API_URL;

const GET_RETRY_METHODS = new Set(['GET', 'HEAD']);
/** Safe to retry once in dev when the API process restarts mid-request. */
const DEV_WRITE_RETRY_METHODS = new Set(['PATCH', 'PUT']);

function isRetryableNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof Error) {
    return (
      err.message.includes('503') ||
      err.message.includes('502') ||
      err.message.includes('restarting')
    );
  }
  return false;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const maxAttempts = import.meta.env.DEV
    ? GET_RETRY_METHODS.has(method)
      ? 4
      : DEV_WRITE_RETRY_METHODS.has(method)
        ? 2
        : 1
    : 1;

  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await requestOnce<T>(path, init);
    } catch (err) {
      lastErr = err;
      if (attempt >= maxAttempts - 1 || !isRetryableNetworkError(err)) throw err;
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  throw lastErr;
}

async function requestOnce<T>(path: string, init: RequestInit = {}, retried401 = false): Promise<T> {
  const token = localStorage.getItem('sb-access-token');

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  if (init.body !== undefined && init.body !== null && init.body !== '') {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  } catch (err) {
    if (import.meta.env.DEV) {
      throw new TypeError(
        err instanceof Error ? err.message : 'Network error — API may be restarting'
      );
    }
    throw err;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) {
      if (!retried401) {
        const refreshed = await tryRefreshSession();
        if (refreshed?.access_token) {
          localStorage.setItem('sb-access-token', refreshed.access_token);
          return requestOnce<T>(path, init, true);
        }
      }
      if (!useAuthStore.getState().signingOut) {
        handleSessionExpired();
      }
      throw new Error('Session expired — please sign in again');
    }
    if (res.status === 429) {
      throw new Error('HTTP 429 — too many requests, try again in a moment');
    }
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path: string, body?: unknown) =>
    request<void>(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
};
